import { supabase } from "./core.js";
import type { Profile } from "../types/index.js";

export function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}
export function signOut() {
  return supabase.auth.signOut();
}

export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      "id, full_name, email, cpf, phone, active, profile_risk, role, client_id, onboarding_done, lgpd_consent, lgpd_consent_at, totp_enabled, totp_enforced, created_at, updated_at"
    )
    .eq("id", userId)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data as Profile | null;
}

export async function fetchAllProfiles(): Promise<
  Pick<Profile, "id" | "full_name" | "client_id" | "role" | "email">[]
> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, client_id, role, email")
    .order("full_name");
  if (error) throw error;
  return data || [];
}

export async function markOnboardingDone(): Promise<void> {
  const { error } = await supabase.rpc("mark_onboarding_done");
  if (error) throw error;
}

interface CreateClientBody {
  nome: string;
  email: string;
  cpf?: string;
  phone?: string;
  [key: string]: unknown;
}

interface ApiResponse {
  success: boolean;
  error?: string;
  [key: string]: unknown;
}

export async function invokeCreateClient(body: CreateClientBody): Promise<ApiResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sessão expirada. Faça login novamente.");

  const response = await fetch("/api/create-client", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  const data: ApiResponse = await response.json();
  if (!data?.success)
    throw new Error(data?.error || `Erro ao criar cliente (HTTP ${response.status})`);
  return data;
}

export async function invokeConfirmPasswordChange(newPassword: string): Promise<ApiResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sessão expirada. Faça login novamente.");

  const response = await fetch("/api/confirm-password-change", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ newPassword }),
  });

  const data: ApiResponse = await response.json();
  if (!data?.success)
    throw new Error(data?.error || `Erro ao trocar senha (HTTP ${response.status})`);
  return data;
}

export async function invokeRemoveMustChangePassword(): Promise<ApiResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sessão expirada. Faça login novamente.");

  const response = await fetch("/api/remove-must-change-password", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  const data: ApiResponse = await response.json();
  if (!data?.success)
    throw new Error(
      data?.error || `Erro ao remover flag must_change_password (HTTP ${response.status})`
    );
  return data;
}
